import React, { FC } from 'react';
import { NodeViewWrapper, NodeViewProps, NodeViewContent } from '@tiptap/react';
import styled from '@emotion/styled';
import { Chip, Paper, Stack, Typography } from '@mui/material';
import { Image as ImageIcon, Warning } from '@mui/icons-material';
import { GuideFigureDialog, GuideFigureDialogProps, GuideFigureDialogResult } from '../../../figures/GuideFigureDialog';
import { useModal } from '../../../../../dialogs/useModal';
import { getHtmlId, getTypeDescription, useNumberedNode } from '../../numbering';
import { capitalizeFirstLetter } from '../../../../../utils/string';
import { BlockNode } from '../BlockNode';
import { theme } from '../../../../../theme';
import { ImageFigureAttributes } from '../plugins/ImageFigure';
import { getFileNames, getMissingFileWarning, getThumbnailUrlFromImageFigure, mapImageFigureFromAttributes } from '../../../figures/figureHelper';
import { useConfig } from '../../../../../env';
import { useGuideCmsContext } from '../../../GuideCmsContext';
import { BlockNodeToolbar } from './BlockNodeToolbar';

const StyledPaper = styled(Paper)`
  display: inline-block;
  max-width: 800px;
  padding: ${theme.spacing(2)};
  margin: ${theme.spacing(1, 0)};
  & > img {
    margin-bottom: ${theme.spacing(2)};
    max-height: 300px;
  }
  & figcaption {
    position: relative;
    margin: 10px 0;
    padding: 5px 10px;
    border: 1px solid #bdbdbd;
    width: fit-content;
    min-width: 80px;

    &:before {
      font-size: 12px;
      color: #666;
      position: absolute;
      top: -10px;
      background-color: white;
      content: 'Figurtekst';
    }
  }
`;

const StyledPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 200px;
  height: 150px;
  background-color: #f5f5f5;
  border: 1px dashed #bdbdbd;
  margin-bottom: ${theme.spacing(2)};
`;

export const ImageFigureView: FC<NodeViewProps> = (props) => {
  const attributes = props.node.attrs as ImageFigureAttributes;
  const config = useConfig();
  const { guideId } = useGuideCmsContext();

  const modal = useModal<GuideFigureDialogProps, GuideFigureDialogResult>({ data: { isNew: false } });
  const node = useNumberedNode(attributes.id ?? '', props.editor);
  const imageFigure = mapImageFigureFromAttributes(attributes);
  const thumbnailUrl = getThumbnailUrlFromImageFigure(config, guideId, imageFigure);
  const fileNames = getFileNames(imageFigure);
  const fileWarning = getMissingFileWarning(imageFigure);


  const renderImage = () => {
    if (thumbnailUrl) {
      return <img src={thumbnailUrl} />;
    }
    return (
      <StyledPlaceholder>
        <ImageIcon color="disabled" sx={{ fontSize: 48 }} />
      </StyledPlaceholder>
    );
  };

  return (
    <>
      {modal.isOpen && <GuideFigureDialog {...modal} />}
      <NodeViewWrapper className="react-component">
        <BlockNode props={props}>
          <StyledPaper id={node ? getHtmlId(node) : ''} elevation={3}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="h6" color="text.secondary">
                {capitalizeFirstLetter(getTypeDescription('imageFigure'))} {node?.numbering}
              </Typography>
              {props.editor.isEditable && (
                <BlockNodeToolbar
                  onEdit={() => modal.open({ editFigureId: attributes.figureId ?? undefined, isNew: false })}
                  editTooltip="Rediger figur"
                  onDelete={() => props.deleteNode()}
                  deleteTooltip="Slett figur"
                />
              )}
            </Stack>
            <div style={{ position: 'relative', width: 'fit-content' }}>
              {renderImage()}
              {fileWarning && (
                <Chip label={fileWarning} size="small" color="warning" icon={<Warning />} sx={{ position: 'absolute', top: 6, left: 6 }} />
              )}
            </div>
            {fileNames.small && (
              <Typography variant="body2" color="text.secondary">
                Liten: {fileNames.small}
              </Typography>
            )}
            {fileNames.large && (
              <Typography variant="body2" color="text.secondary">
                Stor: {fileNames.large}
              </Typography>
            )}
            <NodeViewContent className="content" as="div"></NodeViewContent>
          </StyledPaper>
        </BlockNode>
      </NodeViewWrapper>
    </>
  );
};
